import { makeObservable, observable, action } from 'mobx';

import {
  EMOJI_PRELOAD_TIMEOUT_MS,
  EMOJI_PRELOAD_BATCH_SIZE
} from 'widget/consts/emoji';
import { emojiToCdnUrl } from 'widget/utils/emoji';

type ImageStatus = 'loading' | 'loaded' | 'failed';

type PrivateFields = '_flushBatch' | '_markAllFailed';

export class EmojiStore {
  private _statuses = observable.map<string, ImageStatus>();

  constructor() {
    makeObservable<EmojiStore, PrivateFields>(this, {
      markAsFailed: action,
      destroy: action,
      _flushBatch: action,
      _markAllFailed: action
    });
  }

  getStatus(emoji: string): ImageStatus | undefined {
    return this._statuses.get(emoji);
  }

  isImageLoaded(emoji: string): boolean {
    return this._statuses.get(emoji) === 'loaded';
  }

  markAsFailed(emoji: string): void {
    this._statuses.set(emoji, 'failed');
  }

  preloadInBatches({
    emojiList,
    batchSize = EMOJI_PRELOAD_BATCH_SIZE,
    timeout = EMOJI_PRELOAD_TIMEOUT_MS
  }: {
    emojiList: string[];
    batchSize?: number;
    timeout?: number;
  }): () => void {
    const cancelledRef = { value: false };

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this._markAllFailed(emojiList);

      return () => {
        cancelledRef.value = true;
      };
    }

    const pending = new Map<string, ImageStatus>();
    const allPromises = emojiList.map((emoji) =>
      this._loadImage({ emoji, pending, timeout })
    );

    this._scheduleBatches({
      emojiList,
      allPromises,
      pending,
      batchSize,
      cancelled: cancelledRef
    });

    return () => {
      cancelledRef.value = true;
    };
  }

  destroy(): void {
    this._statuses.clear();
  }

  private _markAllFailed(emojiList: string[]): void {
    for (const emoji of emojiList) {
      this._statuses.set(emoji, 'failed');
    }
  }

  private _flushBatch(results: Map<string, ImageStatus>): void {
    results.forEach((status, emoji) => {
      this._statuses.set(emoji, status);
    });
  }

  private _loadImage({
    emoji,
    pending,
    timeout
  }: {
    emoji: string;
    pending: Map<string, ImageStatus>;
    timeout: number;
  }): Promise<void> {
    const url = emojiToCdnUrl(emoji);

    if (!url) {
      pending.set(emoji, 'failed');

      return Promise.resolve();
    }

    const existing = this._statuses.get(emoji);

    if (existing === 'loaded' || existing === 'failed') {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      let settled = false;

      const settle = (status: ImageStatus) => {
        if (settled) {
          return;
        }

        settled = true;
        clearTimeout(timer);
        pending.set(emoji, status);
        resolve();
      };

      const img = new Image();

      img.onload = () => settle('loaded');
      img.onerror = () => settle('failed');

      const timer = setTimeout(() => settle('failed'), timeout);

      img.src = url;
    });
  }

  /**
   * Нарезает промисы на пачки. Когда все промисы пачки резолвятся,
   * собирает результаты из pending и flush'ит в observable map одним action.
   */
  private _scheduleBatches({
    emojiList,
    allPromises,
    pending,
    batchSize,
    cancelled
  }: {
    emojiList: string[];
    allPromises: Promise<void>[];
    pending: Map<string, ImageStatus>;
    batchSize: number;
    cancelled: { value: boolean };
  }): void {
    for (let i = 0; i < emojiList.length; i += batchSize) {
      const batchEmojis = emojiList.slice(i, i + batchSize);
      const batchPromises = allPromises.slice(i, i + batchSize);

      Promise.all(batchPromises).then(() => {
        if (cancelled.value) {
          return;
        }

        const batchResults = new Map<string, ImageStatus>();

        for (const emoji of batchEmojis) {
          const status = pending.get(emoji);

          if (status) {
            batchResults.set(emoji, status);
          }
        }

        this._flushBatch(batchResults);
      });
    }
  }
}

// Lazy-singleton: создаётся при первом вызове.
// Используется как fallback для useEmojiStore() и как
// прямой доступ для DOM-кода (emojiNodes.ts).
let emojiStoreInstance: EmojiStore | null = null;

export function getEmojiStore(): EmojiStore {
  if (!emojiStoreInstance) {
    emojiStoreInstance = new EmojiStore();
  }

  return emojiStoreInstance;
}

export { type ImageStatus };
